import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, computed, effect, inject, input, OnInit, output, PLATFORM_ID, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { interval, of, timer } from 'rxjs';
import { concatMap, filter, startWith, tap } from 'rxjs/operators';
import { WindowVisibilityService } from '../../../../services/window-visibility.service';
import { isNullOrUndefined } from '../../../../shared/adv-utils';
import { YsCarouselImageComponent } from '../ys-carousel-image/ys-carousel-image.component';
import { initialYsCarouselConfig, SwipeStart, YsCarouselClickedEvent, YsCarouselConfig, YsCarouselState, YsCarouselTile, YsCarouselTileInternal } from './ys-carousel.model';


/**
 * YsCarouselComponent provides a high end image carousel with support for lazy loading,
 * animation, pagination, progress bar, and more.
 * It is based on classic approach.
 * However,
 * - it has a reasonable DOM footprint
 * - provides information on loading images
 * - this is especially useful with lazy loading on slow lines
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'ys-carousel',
  imports: [
    MatProgressBarModule,
    MatButtonModule,
    MatIconModule,
    YsCarouselImageComponent
  ],
  templateUrl: './ys-carousel.component.html',
  styleUrls: ['./ys-carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class YsCarouselComponent implements OnInit, AfterViewInit {
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly windowVisibilityService = inject(WindowVisibilityService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  // Signal-based inputs
  config = input<YsCarouselConfig | null>(null);
  repo = input<string | null>(null);
  tiles = input<YsCarouselTile[]>([]);

  // Signal-based output
  clicked = output<YsCarouselClickedEvent>();

  public internalConfig: YsCarouselConfig = { ...initialYsCarouselConfig }
  public internalTiles: YsCarouselTileInternal[] = [];

  // Reactive state with signals
  public index = signal(0);
  public state = signal(YsCarouselState.NOT_PLAYING);
  public enableTransition = signal(true);
  private hasStartedPlaying = signal(false);

  // Slider position (manual signal to control rewind timing)
  public sliderPositionPercent = signal(0);

  // Computed styles for template binding
  public transformStyle = computed(() => `translate3d(-${this.sliderPositionPercent()}%, 0, 0)`);
  public transitionStyle = computed(() =>
    this.enableTransition()
      ? `transform ${this.internalConfig.transitionTimeMs ?? 1000}ms ease-in-out`
      : 'none'
  );

  // Observables for autoplay (created in ngOnInit with configured timing)
  private runningInner$ = of(null);
  private runningInitial$ = of(null);
  private runningRestart$ = of(null);


  /**
   * Swipe status
   */
  private lastSwipeStart: SwipeStart | null = null;

  constructor() {
    // Reactive autoplay management with effect
    effect((onCleanup) => {
      // Only run in browser
      if (!this.isBrowser) return;

      // Need tiles and autoplay enabled
      if (this.internalTiles.length <= 1) return;
      if (!this.internalConfig.autoplay) return;

      // Only subscribe when PLAYING
      if (this.state() !== YsCarouselState.PLAYING) return;

      // Choose observable based on whether we've started before
      const observable = this.hasStartedPlaying()
        ? this.runningRestart$
        : this.runningInitial$;

      // Mark as started
      this.hasStartedPlaying.set(true);

      // Subscribe to autoplay
      const sub = observable.subscribe();

      // Cleanup on state change or destruction
      onCleanup(() => {
        sub.unsubscribe();
      });
    });
  }

  ngOnInit(): void {
    // configuration - merge input config with defaults
    const inputConfig = this.config();
    if (inputConfig) {
      this.internalConfig = {
        ...initialYsCarouselConfig,
        ...inputConfig,
        autoplay: inputConfig.autoplay ?? initialYsCarouselConfig.autoplay,
        transitionTimeMs: inputConfig.transitionTimeMs ?? initialYsCarouselConfig.transitionTimeMs,
        presentationTimeMs: inputConfig.presentationTimeMs ?? initialYsCarouselConfig.presentationTimeMs,
        progressIndicator: inputConfig.progressIndicator ?? initialYsCarouselConfig.progressIndicator,
        pagination: inputConfig.pagination ?? initialYsCarouselConfig.pagination,
        controls: inputConfig.controls ?? initialYsCarouselConfig.controls,
        playButton: inputConfig.playButton ?? initialYsCarouselConfig.playButton,
        onClickPausePresentation: inputConfig.onClickPausePresentation ?? initialYsCarouselConfig.onClickPausePresentation,
      }
    }

    // Create observables with configured timing values
    this.runningInner$ = of(null).pipe(
      tap(() => {
        this.index.update(i => i + 1);
        const newIndex = this.index();
        // Update position to show next slide
        this.sliderPositionPercent.set((newIndex / (this.internalTiles.length + 1)) * 100);

        if (newIndex >= this.internalTiles.length) {
          // Use timer (RxJS) instead of setTimeout for SSR safety
          timer(this.internalConfig.transitionTimeMs ?? 1000).subscribe(() => {
            // Instant reset: disable transition first
            this.enableTransition.set(false);

            if (this.isBrowser) {
              // Use requestAnimationFrame to ensure browser paints the transition:none style
              // before we reset the position. This prevents visible rewind.
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  // Double RAF ensures paint has happened
                  this.index.set(0);
                  this.sliderPositionPercent.set(0);

                  // Re-enable transitions AFTER reset position is painted
                  requestAnimationFrame(() => {
                    this.enableTransition.set(true);
                  });
                });
              });
            } else {
              // SSR fallback
              this.index.set(0);
              this.sliderPositionPercent.set(0);
              this.enableTransition.set(true);
            }
          });
        }
        this.state.set(YsCarouselState.PLAYING);
      })
    );

    // Observable running the presentation initially
    this.runningInitial$ = interval(this.internalConfig.presentationTimeMs).pipe(
      filter(() => this.windowVisibilityService.visibilityState() === 'visible'),
      concatMap(() => this.runningInner$)
    );

    // Observable running the presentation after restart
    this.runningRestart$ = interval(this.internalConfig.presentationTimeMs).pipe(
      startWith(0),
      concatMap(() => this.runningInner$)
    );

    // tiles
    if (!isNullOrUndefined(this.tiles())) {
      this.internalTiles = this.tiles().map(tile => ({
        styleClass: tile.styleClass,
        src: tile.img?.src,
        // width: tile.img.width,
        // height: tile.img.height,
        alt: tile.img?.alt ? tile.img.alt : 'TILE',
        // style: tile.img.style ? tile.img.style : `object-fit:cover;object-position:${tile.img.objectPosition ?? '50% 50%'};`,
        style: `object-fit:cover;object-position:${tile.img?.objectPosition ?? '50% 50%'};`,
        class: tile.img?.class,
        // srcset: tile.img.srcset?.join(',') ?? this.createImgSrcSet(tile.img.src, tile.img.breakpoints),
        sizes: tile.img?.sizes?.join(','),
        loading: tile.img?.loading,
        priority: tile.img?.priority,
        // fill: tile.img.fill,
        crossOrigin: undefined,
        safeHtml: this.domSanitizer.bypassSecurityTrustHtml(tile.html ?? '')
      }))
    }
  }

  ngAfterViewInit(): void {
    // Declarative: Just set state to PLAYING, effect handles subscription
    if (this.isBrowser && this.internalTiles.length > 1 && this.internalConfig.autoplay === true) {
      this.state.set(YsCarouselState.PLAYING);
    }
  }

  /**
   * click handler, called from template when a tile is clicked (declarative)
   * - emits clicked event with index, tile and state information
   * - if configured, pauses the presentation when a tile is clicked
   */
  handleClick(): void {
    const currentState = this.state();

    switch (currentState) {
      case YsCarouselState.PLAYING:
        if (this.internalConfig.onClickPausePresentation === true) {
          this.state.set(YsCarouselState.PAUSED);
        }
        break;
      case YsCarouselState.PAUSED:
        this.state.set(YsCarouselState.PLAYING);
        break;
      case YsCarouselState.NOT_PLAYING:
        break;
    }

    const idx = (this.index() >= this.tiles().length) ? this.index() - 1 : this.index();
    this.clicked.emit({
      index: idx,
      tile: { ...this.tiles()[idx] },
      state: this.state()
    })
  }

  /**
   * play the carousel, called from template when play button is clicked
   */
  play(): void {
    this.handleClick();
  }

  /**
   * move to next left tile, called from template when left button is clicked (declarative)
   * - if currently playing, it will be paused
   *
   */
  left(): void {
    if (this.state() === YsCarouselState.PLAYING) {
      this.state.set(YsCarouselState.PAUSED);
    }
    if (this.index() > 0) {
      this.enableTransition.set(true);
      this.index.update(i => i - 1);
      this.sliderPositionPercent.set((this.index() / (this.internalTiles.length + 1)) * 100);
    }
  }


  /**
   * move to next right tile, called from template when right button is clicked (declarative)
  * - if currently playing, it will be paused
   */
  right(): void {
    if (this.state() === YsCarouselState.PLAYING) {
      this.state.set(YsCarouselState.PAUSED);
    }
    if (this.index() < this.internalTiles.length) {
      this.enableTransition.set(true);
      this.index.update(i => i + 1);
      this.sliderPositionPercent.set((this.index() / (this.internalTiles.length + 1)) * 100);
    }
  }

  /**
   * move to a particular tile by index (declarative)
   * @param idx 
   */
  moveTo(idx: number): void {
    if (idx !== this.index()) {
      if (this.state() === YsCarouselState.PLAYING) {
        this.state.set(YsCarouselState.PAUSED);
      }
      if (idx < this.internalTiles.length) {
        this.enableTransition.set(true);
        this.index.set(idx);
        this.sliderPositionPercent.set((idx / (this.internalTiles.length + 1)) * 100);
      }
    }
  }


  /**
   * swipe handler, called from template when a swipe gesture is detected
   * @param e - the touch event
   * @param what - 'start' or 'end' indicating the phase of the swipe
   * - on swipe start, the initial coordinates and time are stored
   * - on swipe end, the direction and duration are calculated to determine if a swipe occurred
   * - if a valid swipe is detected, the carousel moves to the next or previous tile accordingly
   */
  swipe(e: TouchEvent, what: string): void {
    const coord: [number, number] = [e.changedTouches[0].clientX, e.changedTouches[0].clientY];
    const time = new Date().getTime();
    if (what === 'start') {
      this.lastSwipeStart = { coord, time };
    } else if (what === 'end' && this.lastSwipeStart) {
      const direction = [coord[0] - this.lastSwipeStart.coord[0], coord[1] - this.lastSwipeStart.coord[1]];
      const duration = time - this.lastSwipeStart.time;
      if (duration < 1000 //
        && Math.abs(direction[0]) > 30 // Long enough
        && Math.abs(direction[0]) > Math.abs(direction[1] * 3)) { // Horizontal enough
        const swipe = direction[0] < 0 ? 'next' : 'previous';
        // tslint:disable-next-line: no-console
        //this.logService.info(swipe);
        if (swipe === 'next') {
          this.right();
        } else if (swipe === 'previous') {
          this.left();
        }
      }
    }
  }


}
